import type { DashboardSnapshot } from './dashboardData'

function number(value: number) {
  return value.toLocaleString('pt-BR')
}

function signedPercentage(value?: number) {
  if (value === undefined) return undefined
  if (value === 0) return 'sem variação'
  return `${Math.abs(value)} por cento ${value > 0 ? 'acima' : 'abaixo'}`
}

function humanList(items: string[]) {
  if (items.length < 2) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} e ${items.at(-1)}`
}

export function buildDashboardNarration(snapshot: DashboardSnapshot, operation: string, focus: string[]) {
  const populatedAreas = snapshot.areas.filter(area => area.total > 0)
  const topAreas = populatedAreas.slice(0, 3)
  const totalComparison = signedPercentage(snapshot.totalChange)
  const yearComparison = signedPercentage(snapshot.yearChange)
  const source = snapshot.source === 'reference'
    ? 'base histórica validada'
    : snapshot.source === 'mixed'
      ? 'base histórica combinada aos registros do StarTracker'
      : 'registros atuais do StarTracker'
  const parts = [
    `Leitura do Dashboard Executivo do StarTracker para ${snapshot.label}, na operação ${operation}.`,
    `A fonte deste recorte é a ${source}.`,
    `O volume total é de ${number(snapshot.total)} entregas${totalComparison ? `, ${totalComparison} do período anterior` : ''}.`,
    `Foram registradas ${number(snapshot.alterations)} alterações, equivalentes a ${snapshot.alterationRate} por cento do volume.`,
    `O time digital e as demais áreas somam ${number(snapshot.digitalTotal)} peças digitais: ${number(snapshot.digitalInternal)} da área Digital e ${number(snapshot.digitalOther)} de outras áreas.`,
    `O acumulado do ano é de ${number(snapshot.yearTotal)} entregas${yearComparison ? `, ${yearComparison} do ano anterior` : ''}.`,
  ]

  if (topAreas.length) {
    parts.push(`As áreas com maior volume são ${humanList(topAreas.map(area => `${area.area}, com ${number(area.total)} entregas`))}.`)
  } else {
    parts.push('Ainda não há entregas classificadas por área neste recorte.')
  }

  parts.push(`Insight do período: ${snapshot.insight}`)
  parts.push(focus.length
    ? `Focos para os próximos períodos: ${humanList(focus)}.`
    : 'Não há focos cadastrados para os próximos períodos.')
  parts.push('Fim da leitura do Dashboard Executivo.')

  return parts.join(' ').slice(0, 4096)
}

export async function requestDashboardSpeech(input: string, signal?: AbortSignal) {
  const response = await fetch('/api/dashboard-speech', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ input }),
    signal,
  })

  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null
    throw new Error(body?.error ?? 'Não foi possível gerar a narração por IA.')
  }

  return {
    audio: await response.blob(),
    provider: response.headers.get('X-StarTracker-Speech-Provider') ?? 'ai',
  }
}
